import { IMAGE_TYPE_UPLOAD } from '@/commons/constants';
import UploadNewFiles from '@/components/Modals/ExistedFilesModal/UploadNewFiles';
import UploadedFileContent from '@/components/Modals/ExistedFilesModal/UploadedFileContent';
import YoutubeUrl from '@/components/Modals/ExistedFilesModal/Youtube';
import { getStorageLoading, getStoragePagination } from '@/redux/selectors/storage.selector';
import { Dispatch } from '@/redux/store';
import { FileModel, FileType, PaginationType } from '@/types';
import { Col, message, Radio, RadioChangeEvent, Row, Tabs } from 'antd';
import { FC, useCallback, useState } from 'react';
import { Accept } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';

type Props = {
  selected?: FileModel[];
  setSelected?: any;
  multiple?: boolean;
  accept?: Accept;
  isRefresh?: boolean;
  operations?: any;
  contentTypes?: FileType[];
};

const UPLOAD_NEW_TYPE = {
  UPLOAD_NEW_FILE: { key: 'UPLOAD_NEW_FILE', value: 'Upload new file' },
  YOUTUBE: { key: 'YOUTUBE', value: 'Youtube' },
};

const StorageFileContent: FC<Props> = (props: Props) => {
  const {
    selected,
    setSelected,
    multiple = true,
    accept = IMAGE_TYPE_UPLOAD.accept,
    isRefresh = true,
    operations,
    contentTypes = IMAGE_TYPE_UPLOAD.contentType,
  } = props;
  const [activeTab, setActiveTab] = useState<string>('1');
  const [uploadNewType, setUploadNewType] = useState<string>(UPLOAD_NEW_TYPE.UPLOAD_NEW_FILE.key);
  const dispatch = useDispatch<Dispatch>();
  const pagination: PaginationType = useSelector(getStoragePagination);
  const loading = useSelector(getStorageLoading);

  const handleCheckboxItem = useCallback(
    (item, checked) => {
      if (setSelected) {
        setSelected((prevState) => {
          let newValue = [...prevState];
          if (!multiple) {
            newValue = [];
          }
          const deletedIndex = newValue.findIndex((i) => i.id === item.id);
          if (!checked) {
            if (deletedIndex >= 0) {
              newValue.splice(deletedIndex, 1);
            }
          } else if (deletedIndex < 0) {
            newValue.push(item);
          }
          return newValue;
        });
      }
    },
    [selected]
  );
  const handleUploadDone = useCallback(
    (files: FileModel[]) => {
      if (files && files.length) {
        files.forEach((file) => {
          handleCheckboxItem(file, true);
        });
        handleChangeTabs('1');
        dispatch.storage.filterAsync({ ...pagination, page: 0 });
      }
    },
    [handleCheckboxItem]
  );
  const handleChangeTabs = (key) => {
    setActiveTab(key);
  };
  const handleDeleteStorage = (item) => {
    dispatch.storage.deleteAsync(item).then(() => {
      handleCheckboxItem(item, false);
    });
  };

  const onChange = (e: RadioChangeEvent) => {
    setUploadNewType(e.target.value);
  };

  const handleSubmitYoutube = (url) => {
    if (!url) return;

    const dataRequest: any = {
      type: FileType.YOUTUBE,
      url,
    };

    fetch(`https://noembed.com/embed?dataType=json&url=${url}`)
      .then((res) => res.json())
      .then(async (data) => {
        if (data?.error) {
          message.error('Youtube url is not exist.');
          return;
        }

        dataRequest.name = data.title;
        dataRequest.thumbnail = data.thumbnail_url;

        const res: any = await dispatch.storage.saveAsync(dataRequest);
        if (res) {
          handleCheckboxItem(res, true);
          handleChangeTabs('1');
          dispatch.storage.filterAsync({ ...pagination, page: 0 });
        }
      });
  };

  return (
    <Row>
      <Col span={24}>
        <Tabs
          onChange={handleChangeTabs}
          type="card"
          activeKey={activeTab}
          tabBarExtraContent={operations}
          items={[
            {
              key: '1',
              label: 'Uploaded files',
              disabled: loading,
              children: (
                <UploadedFileContent
                  isRefresh={isRefresh}
                  onSelectItem={handleCheckboxItem}
                  selectedItems={selected}
                  onUpdateSelectedItems={setSelected}
                  contentTypes={contentTypes}
                  onDeleteItem={handleDeleteStorage}
                />
              ),
            },
            {
              key: '2',
              label: 'Upload new',
              disabled: loading,
              children: (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <Radio.Group
                    defaultValue={UPLOAD_NEW_TYPE.UPLOAD_NEW_FILE.key}
                    onChange={onChange}
                    style={{ marginBottom: 16 }}
                  >
                    {Object.values(UPLOAD_NEW_TYPE).map((item) => (
                      <Radio key={item.key} value={item.key}>
                        {item.value}
                      </Radio>
                    ))}
                  </Radio.Group>

                  {activeTab === '2' && uploadNewType === UPLOAD_NEW_TYPE.UPLOAD_NEW_FILE.key && (
                    <UploadNewFiles
                      multiple={multiple}
                      onUploadDone={handleUploadDone}
                      accept={accept}
                    />
                  )}

                  {activeTab === '2' && uploadNewType === UPLOAD_NEW_TYPE.YOUTUBE.key && (
                    <YoutubeUrl onSubmit={(url) => handleSubmitYoutube(url)} />
                  )}
                </div>
              ),
            },
          ]}
        />
      </Col>
    </Row>
  );
};

export default StorageFileContent;
